/**
 * Impersonation token slot — Phase 4 (#112), hardened by #219.
 *
 * Locked design: impersonation tokens have audience `hyder-qms-web` (same as
 * the regular tenant access token) so every tenant route accepts them without
 * modification. To keep the auth client able to pick which token to send AND
 * to drive the impersonation banner, the impersonation token lives in a
 * SEPARATE localStorage slot (`hyder.impersonationToken`) from the regular
 * tenant access token (`hyder.accessToken`) and the platform token
 * (`hyder.platformToken`).
 *
 * Per #219 every minted token is read-only — there is no `mode` field. Any
 * legacy slot still in localStorage carrying a `mode` value is rejected by
 * the shape check and removed.
 *
 * Impersonation tokens do NOT refresh. They have a hard 5-minute TTL — once
 * the JWT's `exp` claim passes, the token must be discarded and the operator
 * bounced back to `/platform`. See `decodeImpersonationToken` for exp extraction.
 */

export const IMPERSONATION_TOKEN_KEY = 'hyder.impersonationToken';

export type ImpersonationSlot = {
  accessToken: string;
  /** ISO-8601 expiry the platform API returns alongside the token. */
  expiresAt: string;
  tenantId: string;
  /** Cached tenant name so the banner doesn't need a round-trip to render. */
  tenantName: string;
  /** Cached platform-admin email so the banner shows who is acting. */
  platformAdminEmail: string;
  /**
   * User-target mode (#186). When set, the banner renders the variant
   * "acting as <targetUserName> (<targetUserRole>)" instead of the wildcard
   * "viewing all of <Tenant>".
   */
  targetUserName?: string;
  targetUserRole?: string;
};

const isImpersonationSlotShape = (v: unknown): v is ImpersonationSlot => {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  if (
    !(
      typeof o.accessToken === 'string' &&
      typeof o.expiresAt === 'string' &&
      typeof o.tenantId === 'string' &&
      typeof o.tenantName === 'string' &&
      typeof o.platformAdminEmail === 'string'
    )
  ) {
    return false;
  }
  // Optional fields — accept absence; reject only on a present-but-wrong type.
  if (o.targetUserName !== undefined && typeof o.targetUserName !== 'string') return false;
  if (o.targetUserRole !== undefined && typeof o.targetUserRole !== 'string') return false;
  return true;
};

export const setImpersonationSlot = (slot: ImpersonationSlot | null): void => {
  if (typeof window === 'undefined') return;
  if (slot) {
    window.localStorage.setItem(IMPERSONATION_TOKEN_KEY, JSON.stringify(slot));
  } else {
    window.localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
  }
  // Notify same-tab listeners. `storage` events only fire across tabs;
  // dispatch a CustomEvent so the banner + token-aware api client both
  // observe in-tab changes.
  window.dispatchEvent(new CustomEvent('hyder:impersonation-changed'));
};

// Snapshot cache (#129): `getImpersonationSlot` is consumed by
// `useImpersonationSlot` via `useSyncExternalStore`, whose contract requires
// the snapshot getter to return a STABLE reference when the data has not
// changed (React docs: "If you return a different value each time, React
// will re-render the component in an infinite loop"). Parsing JSON on every
// call breaks that contract — even when localStorage is unchanged, a fresh
// object is returned, triggering an infinite re-render in any consumer that
// also dispatches state updates (e.g. the countdown ticker in the banner).
//
// We key the cache on the raw localStorage string. A no-write read costs one
// localStorage lookup + one string compare; the JSON parse only runs when
// the slot actually changed.
let cachedRaw: string | null | undefined;
let cachedSlot: ImpersonationSlot | null = null;

export const getImpersonationSlot = (): ImpersonationSlot | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(IMPERSONATION_TOKEN_KEY);
  if (raw === cachedRaw) return cachedSlot;
  if (!raw) {
    cachedRaw = raw;
    cachedSlot = null;
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isImpersonationSlotShape(parsed)) {
      window.localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
      cachedRaw = null;
      cachedSlot = null;
      return null;
    }
    cachedRaw = raw;
    cachedSlot = parsed;
    return parsed;
  } catch {
    window.localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    cachedRaw = null;
    cachedSlot = null;
    return null;
  }
};

/**
 * Decode the impersonation JWT and return the `exp` (Unix seconds) claim. We
 * intentionally do NOT verify the signature here — the API does that on every
 * request. We only need `exp` to drive the countdown in the banner.
 *
 * Returns `null` if the token is malformed.
 */
export const decodeImpersonationExp = (accessToken: string): number | null => {
  try {
    const parts = accessToken.split('.');
    if (parts.length !== 3) return null;
    const payloadPart = parts[1];
    if (!payloadPart) return null;
    // base64url → base64 then decode
    const b64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    const json = atob(padded);
    const parsed: unknown = JSON.parse(json);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { exp?: unknown }).exp === 'number'
    ) {
      return (parsed as { exp: number }).exp;
    }
    return null;
  } catch {
    return null;
  }
};
